
import React, { useState, useContext, useEffect } from 'react'
import { SpinnerComponent } from 'react-element-spinner'
import {
	Button,
	Card,
	CardBody,
	CardFooter,
	CardHeader,
	Col,
	Container,
	Label,
	Row,
	Table
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { library } from "@fortawesome/fontawesome-svg-core"
import { faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

library.add(faTrash)

import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import utc from 'dayjs/plugin/utc'
import timeZone from 'dayjs/plugin/timezone'
dayjs.extend(localizedFormat)
dayjs.extend(utc)
dayjs.extend(timeZone)

import { AuctionsContext } from '../../../../contexts/AuctionsProvider'
import VehiclesService from '../../../../services/VehiclesService'
import SiteService from '../../../../services/SiteService'
import { SessionContext } from '../../../../contexts/SessionProvider'
import { VehiclesContext } from '../../../../contexts/VehiclesProvider'
import AuctionNoteModal from '../../../../components/Modals/AuctionNoteModal'
import {subscribe, unsubscribe} from '../../../../events/customEvents'
import AuctionsService from '../../../../services/AuctionsService'
import VehicleInspectionsService from '../../../../services/VehicleInspectionsService'
import PageTitleContextMenu from '../../../../components/PageTitles/PageTitleContextMenu'
import AuctionsNavBar from '../../../../components/Navbars/AuctionsNavBar'

const AuctionDisclosures = ({ embedded = false, onRequestClose, onSelectDetailTab }) =>
{
	const { t } = useTranslation()
	const [isBusy, setIsBusy] = React.useState(false)
	const auctionsContext = useContext(AuctionsContext)
	const sessionContext = useContext(SessionContext)
	const vehiclesContext = useContext(VehiclesContext)
	const [vehicleInspectionId, setVehicleInspectionId] = useState('')
	const [auctionNote, setAuctionNote] = useState({ auctionNoteId: 0, auctionId: 0, note: '', public: true, validation: { note: false } })
	const [showAuctionNoteModal, setShowAuctionNoteModal] = useState(false)
	const navigate = useNavigate()

	useEffect(() =>
	{
		initializeComponent()

		subscribe('auctionNoteModalClosed', auctionNoteModalClosed)
		
		return () =>
		{
			unsubscribe('auctionNoteModalClosed', auctionNoteModalClosed)
		}
	}, [])

	const initializeComponent = () =>
	{
		retrieveVehicleDisclosures()
		retrieveAuctionNotes()
	}

	const auctionNoteModalClosed = (event) =>
	{
		setShowAuctionNoteModal(false)
		if (event.detail.noteAdded)
		{
			retrieveAuctionNotes()
		}
	}

	const retrieveVehicleDisclosures = () =>
	{
		// Support both nested (legacy) and flat (new AuctionTabs API) structure
		const vehicleId = auctionsContext.auction?.auctionVehicle?.vehicleId || auctionsContext.auction?.vehicleId
		if (!vehicleId)
		{
			console.warn('AuctionDisclosures: vehicleId not available, skipping disclosures fetch')
			return
		}
		setIsBusy(true)
		VehiclesService
			.getDisclosures(vehicleId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				vehiclesContext.setVehicleDisclosures(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}

	const retrieveAuctionNotes = () =>
	{
		setIsBusy(true)
		AuctionsService
			.getAuctionNotes(auctionsContext.auction.auctionId, false, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				auctionsContext.setNotes(result.data.actionResult)
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}

	const handleAddNewNote = () =>
	{
		setAuctionNote({
			auctionNoteId: 0,
			auctionId: auctionsContext.auction.auctionId,
			note: '',
			public: true,
			validation: { note: false }
		})

		setShowAuctionNoteModal(true)
	}

	const handleRemoveNote = (e, auctionNoteId) =>
	{
		e.preventDefault()

		setIsBusy(true)
		AuctionsService
			.removeAuctionNote(auctionNoteId, sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				retrieveAuctionNotes()
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleGoBack = () =>
	{
		if (embedded)
		{
			if (typeof onRequestClose === 'function') onRequestClose()
			return
		}
		navigate('/secure/auctions-listing')
	}

	return (
		<div style={{
			zIndex: 500
		}}>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			{
				!embedded &&
				<PageTitleContextMenu
					heading={ t('Auctions.Disclosures.Title') }
					subheading={ auctionsContext.auction.auctionVehicle.vehicle.year + ' ' +
						auctionsContext.auction.auctionVehicle.vehicle.make + ' ' +
						auctionsContext.auction.auctionVehicle.vehicle.model + ' / ' +
						auctionsContext.auction.auctionVehicle.vehicle.trim + ' / ' +
						auctionsContext.auction.auctionVehicle.vehicle.serialNumber
					}
					icon="pe-7s-ticket icon-gradient bg-premium-dark"
					headerOptions={ { showBack: true, backCommand: () => { handleGoBack() }, showReload: false, reloadCommand: null } }
				/>
			}
			<Container className={ embedded ? "" : "mt--6" } fluid>
				<Row>
					<div className="col">
						<Card>
							<CardHeader style={{padding: '0.5rem 0.75rem'}}>
								<AuctionsNavBar
									parentComponent={'auction-disclosures'}
									onSelect={ embedded ? onSelectDetailTab : undefined }
								/>
							</CardHeader>
							<CardBody>
								<Row>
									<Col md="12">
										<div className="table-responsive">
											<Table className="align-items-center table-flush">
												<thead className="thead-light">
													<tr>
														<th className="sort" scope="col" colSpan={2}>{t('Auctions.Disclosures.Title')}</th>
													</tr>
												</thead>
												<tbody className="list">
													{vehiclesContext.vehicleDisclosures.map((disclosure, idx) => (
														<tr key={ idx } style={{cursor: 'pointer'}}>
															<td>{ disclosure.name }</td>
															<td>{ disclosure.value }</td>
														</tr>
													))}
												</tbody>
											</Table>
										</div>
										{
											vehiclesContext.vehicleDisclosures.length === 0 &&
											<Row style={{ marginTop: '20px' }}>
												<Col md="12" style={{ textAlign: 'center' }}>
													<Label>{ t('Auctions.Disclosures.No_Results') }</Label>
												</Col>
											</Row>
										}
									</Col>
								</Row>
							</CardBody>
							<CardFooter></CardFooter>
						</Card>
					</div>
				</Row>
				<Row>
					<div className="col">
						<Card>
							<CardHeader style={{padding: '0.5rem 0.75rem'}}></CardHeader>
							<CardBody>
								<Row>
									<Col md="12">
										<div className="text-right">
											<Button
												color="secondary"
												size="sm"
												type="button"
												onClick={ () => handleAddNewNote() }
											>
												Add New Note
											</Button>
										</div>
									</Col>
								</Row>
								<Row>
									<Col md="12">
										<div className="table-responsive">
											<Table className="align-items-center table-flush">
												<thead className="thead-light">
													<tr>
														<th scope="col">Date</th>
														<th scope="col">User</th>
														<th scope="col">Note</th>
														<th scope="col">Visibility</th>
														<th scope="col"></th>
													</tr>
												</thead>
												<tbody className="list">
													{auctionsContext.notes.map((auctionNote, idx) => (
														<tr key={ idx } style={{cursor: 'pointer'}}>
															<td>{ dayjs(auctionNote.created).format('MMM DD, YYYY HH:mm') }</td>
															<td>{ auctionNote.createdBy || 'N/A' }</td>
															<td>{ auctionNote.note }</td>
															<td>{ auctionNote.public ? 'Public' : 'Private' }</td>
															<td>
																<Button
																	className="btn-icon-only rounded-circle"
																	color="light"
																	title="Delete Note"
																	onClick={ (event) => handleRemoveNote(event, auctionNote.auctionNoteId) }
																>
																<span className="btn-inner--icon">
																	<FontAwesomeIcon icon={ faTrash } style={{ color: 'red' }} />
																</span>
																</Button>
															</td>
														</tr>
													))}
												</tbody>
											</Table>
										</div>
										{
											auctionsContext.notes.length === 0 &&
											<Row style={{ marginTop: '20px' }}>
												<Col md="12" style={{ textAlign: 'center' }}>
													<Label>{ t('Auctions.Notes.No_Results') }</Label>
												</Col>
											</Row>
										}
									</Col>
								</Row>
							</CardBody>
							<CardFooter></CardFooter>
						</Card>
					</div>
				</Row>
			</Container>

			<AuctionNoteModal
				showAuctionNoteModal={ showAuctionNoteModal }
				auctionNote={ auctionNote }
				setAuctionNote={ setAuctionNote }
			/>
		</div>
	)
}

export default AuctionDisclosures
